import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AdminOrders from "@/components/AdminOrders";
import AdminGiftCards from "@/components/AdminGiftCards";

const WS_STATUSES = ["pending", "approved", "declined"];
const QUOTE_STATUSES = [
  { key: "new", label: "New" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [wholesale, setWholesale] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orderCount, setOrderCount] = useState(0);
  const [rules, setRules] = useState({ min_qty_per_design: 5, opening_order_min: 150, opening_min_first_only: true });
  const [rulesSaving, setRulesSaving] = useState(false);

  // filter/search state
  const [wsStatus, setWsStatus] = useState("pending");
  const [wsQuery, setWsQuery] = useState("");
  const [qStatus, setQStatus] = useState("new");
  const [qQuery, setQQuery] = useState("");

  // expanded application view
  const [openApp, setOpenApp] = useState(null); // wholesale row

  const refresh = () => {
    api.get("/admin/wholesale/all").then(r => setWholesale(r.data)).catch(() => {});
    api.get("/admin/quotes").then(r => setQuotes(r.data)).catch(() => {});
    api.get("/admin/orders").then(r => setOrderCount(r.data.length)).catch(() => {});
    api.get("/wholesale/settings").then(r => setRules(r.data)).catch(() => {});
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  const wsFiltered = useMemo(() => {
    const q = wsQuery.trim().toLowerCase();
    return wholesale
      .filter(w => (w.status || "pending") === wsStatus)
      .filter(w => !q ||
        (w.business_name || "").toLowerCase().includes(q) ||
        (w.name || "").toLowerCase().includes(q) ||
        (w.email || "").toLowerCase().includes(q)
      );
  }, [wholesale, wsStatus, wsQuery]);

  const wsCounts = useMemo(() => {
    const c = { pending: 0, approved: 0, declined: 0 };
    wholesale.forEach(w => { c[w.status || "pending"] = (c[w.status || "pending"] || 0) + 1; });
    return c;
  }, [wholesale]);

  const qFiltered = useMemo(() => {
    const s = qQuery.trim().toLowerCase();
    return quotes
      .filter(q => (q.status || "new") === qStatus)
      .filter(q => !s ||
        (q.name || "").toLowerCase().includes(s) ||
        (q.email || "").toLowerCase().includes(s) ||
        (q.project_type || "").toLowerCase().includes(s)
      );
  }, [quotes, qStatus, qQuery]);

  const qCounts = useMemo(() => {
    const c = { new: 0, in_progress: 0, completed: 0 };
    quotes.forEach(q => { c[q.status || "new"] = (c[q.status || "new"] || 0) + 1; });
    return c;
  }, [quotes]);

  if (loading) return <div className="p-24 text-brand-neutral">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const decideWholesale = async (id, status) => {
    await api.post(`/admin/wholesale/${id}/decision`, { status });
    toast.success(`Application ${status}. Email sent.`);
    setOpenApp(null);
    refresh();
  };

  const setQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}/status`, { status });
      toast.success(`Marked ${status.replace("_", " ")}.`);
      refresh();
    } catch { toast.error("Could not update status."); }
  };

  const saveRules = async () => {
    setRulesSaving(true);
    try {
      const { data } = await api.patch("/admin/wholesale/settings", {
        min_qty_per_design: parseInt(rules.min_qty_per_design, 10),
        opening_order_min: parseFloat(rules.opening_order_min),
        opening_min_first_only: !!rules.opening_min_first_only,
      });
      setRules(data);
      toast.success("Wholesale rules updated.");
    } catch { toast.error("Could not save rules."); }
    finally { setRulesSaving(false); }
  };

  return (
    <div data-testid="admin-page" className="max-w-[1440px] mx-auto px-6 md:px-12 pt-16 pb-24 fade-up">
      <p className="overline hidden">Studio Admin</p>
      <h1 className="font-serif font-light text-3xl text-brand-dark mt-3">Dashboard</h1>

      <Tabs defaultValue="wholesale" className="mt-10">
        <TabsList>
          <TabsTrigger data-testid="tab-wholesale" value="wholesale">Wholesale ({wsCounts.pending})</TabsTrigger>
          <TabsTrigger data-testid="tab-quotes" value="quotes">Commissions ({qCounts.new})</TabsTrigger>
          <TabsTrigger data-testid="tab-orders" value="orders">Orders ({orderCount})</TabsTrigger>
          <TabsTrigger data-testid="tab-gift-cards" value="gift-cards">Gift Cards</TabsTrigger>
          <TabsTrigger data-testid="tab-rules" value="rules">Rules</TabsTrigger>
        </TabsList>

        {/* --- WHOLESALE --- */}
        <TabsContent value="wholesale" className="mt-8">
          <div className="flex flex-wrap items-center gap-6 mb-6">
            <div className="flex items-center gap-4">
              {WS_STATUSES.map(s => (
                <button
                  key={s}
                  data-testid={`ws-filter-${s}`}
                  onClick={() => setWsStatus(s)}
                  className={`text-xs uppercase tracking-widest ${wsStatus === s ? "text-brand-accent" : "text-brand-neutral hover:text-brand-dark"}`}
                >
                  {s} ({wsCounts[s] || 0})
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 border-b border-brand-border pb-1 ml-auto min-w-[260px]">
              <Search strokeWidth={1.5} className="w-4 h-4 text-brand-neutral" />
              <input
                data-testid="ws-search"
                value={wsQuery}
                onChange={e => setWsQuery(e.target.value)}
                placeholder="Search business or contact"
                className="bg-transparent flex-1 py-1 outline-none text-sm"
              />
            </div>
          </div>

          {wsFiltered.length === 0 ? (
            <p className="text-brand-neutral">No applications in this view.</p>
          ) : (
            <div className="border-t border-brand-border">
              {wsFiltered.map(w => {
                const expanded = openApp?.id === w.id;
                return (
                  <div key={w.id} data-testid={`ws-row-${w.id}`} className="border-b border-brand-border py-4">
                    <button
                      onClick={() => setOpenApp(expanded ? null : w)}
                      className="w-full text-left grid grid-cols-12 items-center gap-4"
                    >
                      <div className="col-span-4">
                        <p className="font-serif text-lg text-brand-dark">{w.business_name || w.name}</p>
                        <p className="text-xs text-brand-neutral">{w.email}</p>
                      </div>
                      <div className="col-span-2 text-sm text-brand-neutral">{w.business_type || "—"}</div>
                      <div className="col-span-2 text-sm text-brand-neutral">{w.position || "—"}</div>
                      <div className="col-span-2 text-xs text-brand-neutral">
                        {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                      </div>
                      <div className="col-span-2 text-right">
                        <span className={`text-[10px] tracking-widest uppercase px-3 py-1 border ${
                          w.status === "approved" ? "border-brand-accent text-brand-accent" :
                          w.status === "declined" ? "border-brand-neutral text-brand-neutral" :
                          "border-brand-dark text-brand-dark"
                        }`}>{w.status || "pending"}</span>
                      </div>
                    </button>

                    {expanded && (
                      <div data-testid={`ws-detail-${w.id}`} className="mt-6 grid md:grid-cols-2 gap-8 bg-brand-surface p-6 paper-texture">
                        <dl className="space-y-2 text-sm">
                          {[
                            ["Business", w.business_name],
                            ["Contact", w.name],
                            ["Position / Role", w.position],
                            ["Email", w.email],
                            ["Phone", w.phone],
                            ["Business Type", w.business_type],
                            ["Website", w.website],
                            ["Island / Country", w.island || w.country],
                            ["VAT / Reg #", w.vat_number],
                            ["Submitted", w.created_at ? new Date(w.created_at).toLocaleString() : "—"],
                          ].map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-6 border-b border-brand-border py-1.5">
                              <dt className="text-[11px] tracking-widest uppercase text-brand-neutral">{k}</dt>
                              <dd className="text-brand-dark text-right">{v || "—"}</dd>
                            </div>
                          ))}
                        </dl>
                        <div className="flex flex-col justify-between">
                          <p className="text-xs text-brand-neutral leading-relaxed">
                            Approving activates their wholesale pricing sitewide and emails a branded welcome.
                            Declining sends a polite decline email — the account will not gain access.
                          </p>
                          <div className="mt-6 flex gap-3">
                            <button data-testid={`approve-${w.id}`} onClick={() => decideWholesale(w.id, "approved")} className="btn-primary">Approve</button>
                            <button data-testid={`reject-${w.id}`} onClick={() => decideWholesale(w.id, "declined")} className="btn-outline">Decline</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* --- COMMISSIONS / QUOTES --- */}
        <TabsContent value="quotes" className="mt-8">
          <div className="flex flex-wrap items-center gap-6 mb-6">
            <div className="flex items-center gap-4">
              {QUOTE_STATUSES.map(s => (
                <button
                  key={s.key}
                  data-testid={`q-filter-${s.key}`}
                  onClick={() => setQStatus(s.key)}
                  className={`text-xs uppercase tracking-widest ${qStatus === s.key ? "text-brand-accent" : "text-brand-neutral hover:text-brand-dark"}`}
                >
                  {s.label} ({qCounts[s.key] || 0})
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 border-b border-brand-border pb-1 ml-auto min-w-[260px]">
              <Search strokeWidth={1.5} className="w-4 h-4 text-brand-neutral" />
              <input
                data-testid="q-search"
                value={qQuery}
                onChange={e => setQQuery(e.target.value)}
                placeholder="Search name, email or project"
                className="bg-transparent flex-1 py-1 outline-none text-sm"
              />
            </div>
          </div>

          {qFiltered.length === 0 ? (
            <p className="text-brand-neutral">No commissions in this view.</p>
          ) : (
            <div className="space-y-4">
              {qFiltered.map(q => (
                <div key={q.id} data-testid={`quote-${q.id}`} className="border border-brand-border p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg text-brand-dark">{q.name}</p>
                      <p className="text-xs text-brand-neutral">
                        {q.email} {q.phone ? `· ${q.phone}` : ""} · {q.project_type || "—"}
                      </p>
                      <p className="text-xs text-brand-neutral mt-1">
                        Ref {q.reference || (q.id || "").slice(0, 6).toUpperCase()} · Submitted {q.created_at ? new Date(q.created_at).toLocaleDateString() : "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-[11px] tracking-widest uppercase text-brand-neutral">Status</label>
                      <select
                        data-testid={`q-status-${q.id}`}
                        value={q.status || "new"}
                        onChange={(e) => setQuoteStatus(q.id, e.target.value)}
                        className="border border-brand-border px-3 py-1.5 text-sm bg-white"
                      >
                        {QUOTE_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <p className="mt-4 text-brand-neutral leading-relaxed text-sm whitespace-pre-wrap">
                    {q.project_description || q.project_details || "—"}
                  </p>
                  <p className="mt-3 text-xs text-brand-neutral">
                    Timeline: {q.completion_date || q.timeline || "—"} · Budget: {q.budget || "—"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* --- ORDERS --- */}
        <TabsContent value="orders" className="mt-8">
          <AdminOrders />
        </TabsContent>

        {/* --- GIFT CARDS --- */}
        <TabsContent value="gift-cards" className="mt-8">
          <AdminGiftCards />
        </TabsContent>

        {/* --- WHOLESALE RULES --- */}
        <TabsContent value="rules" className="mt-8">
          <div className="max-w-2xl">
            <p className="overline">Wholesale purchasing rules</p>
            <h2 className="font-serif font-light text-2xl md:text-3xl text-brand-dark mt-4 leading-tight">
              Set the minimums for approved retailers.
            </h2>
            <p className="mt-4 text-brand-neutral text-sm leading-relaxed">
              These rules apply only to approved wholesale accounts and are enforced in the cart,
              at checkout, and again on the server. Retail customers are unaffected.
            </p>

            <div className="mt-10 space-y-6">
              <label className="block">
                <span className="overline block mb-2">Minimum quantity per design</span>
                <input
                  data-testid="rule-min-qty"
                  type="number"
                  min={1}
                  value={rules.min_qty_per_design}
                  onChange={(e) => setRules({ ...rules, min_qty_per_design: e.target.value })}
                  className="flushed-input w-40"
                />
                <p className="text-xs text-brand-neutral mt-2">Each greeting card design must be ordered in this quantity or more.</p>
              </label>

              <label className="block">
                <span className="overline block mb-2">Opening order minimum ($)</span>
                <input
                  data-testid="rule-opening-min"
                  type="number"
                  min={0}
                  step={0.01}
                  value={rules.opening_order_min}
                  onChange={(e) => setRules({ ...rules, opening_order_min: e.target.value })}
                  className="flushed-input w-40"
                />
                <p className="text-xs text-brand-neutral mt-2">Subtotal (before VAT and shipping) that must be met.</p>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="rule-first-only"
                  type="checkbox"
                  checked={!!rules.opening_min_first_only}
                  onChange={(e) => setRules({ ...rules, opening_min_first_only: e.target.checked })}
                  className="mt-1 accent-brand-accent"
                />
                <span>
                  <span className="text-brand-dark text-sm font-medium">Apply the opening minimum only to first-time wholesale orders</span>
                  <span className="block text-xs text-brand-neutral mt-1">Uncheck to enforce the minimum on every wholesale order.</span>
                </span>
              </label>
            </div>

            <button
              data-testid="rules-save-btn"
              onClick={saveRules}
              disabled={rulesSaving}
              className="btn-primary mt-10"
            >
              {rulesSaving ? "Saving…" : "Save Rules"}
            </button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
